import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { useMerchant } from "../../hooks/useMerchant";
import { useTRPC } from "../../trpc/client";
import { toaster } from "../ui/toaster";

const maskSecret = (value: string) => {
  if (value.length <= 12) return value;
  return `${value.slice(0, 4)}…${value.slice(-4)}`;
};

const ApiTab = (): JSX.Element => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { merchant } = useMerchant();
  const merchantId = merchant?.id ?? "";

  const credentials = useQuery({
    ...trpc.merchants.getCredentials.queryOptions({
      rentalsMerchantId: merchantId,
    }),
    enabled: !!merchant?.id,
  });

  const [webhookUrl, setWebhookUrl] = useState("");
  useEffect(() => {
    setWebhookUrl(credentials.data?.webhook?.url ?? "");
  }, [credentials.data?.webhook?.url]);

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: trpc.merchants.getCredentials.queryKey({
        rentalsMerchantId: merchantId,
      }),
    });

  const notify = (title: string, type: "success" | "error" = "success") =>
    toaster.create({
      title,
      type,
      duration: 5000,
      meta: { closable: true },
    });

  const apiKey = credentials.data?.apiKey ?? null;
  const webhook = credentials.data?.webhook ?? null;

  const setApiKey = useMutation(
    trpc.merchants.setApiKey.mutationOptions({
      onSuccess: async () => {
        const created = !apiKey;
        await invalidate();
        notify(created ? "API key creada" : "API key regenerada");
      },
      onError: (error) => notify(error.message, "error"),
    })
  );

  const setWebhook = useMutation(
    trpc.merchants.setWebhook.mutationOptions({
      onSuccess: async () => {
        const created = !webhook;
        await invalidate();
        notify(created ? "Webhook creado" : "Webhook actualizado");
      },
      onError: (error) => notify(error.message, "error"),
    })
  );

  const removeWebhook = useMutation(
    trpc.merchants.removeWebhook.mutationOptions({
      onSuccess: async () => {
        await invalidate();
        notify("Webhook eliminado");
      },
      onError: (error) => notify(error.message, "error"),
    })
  );

  const rotateWebhookSecret = useMutation(
    trpc.merchants.rotateWebhookSecret.mutationOptions({
      onSuccess: async () => {
        await invalidate();
        notify("Webhook secret regenerado");
      },
      onError: (error) => notify(error.message, "error"),
    })
  );

  const copy = async (value: string) => {
    await navigator.clipboard.writeText(value);
    notify("Copiado");
  };

  return (
    <VStack gap={6} align="stretch">
      <Box>
        <Text fontWeight={600} mb={1}>
          API key
        </Text>
        <Text fontSize="sm" color="gray.500" mb={3}>
          Úsala para autenticar las llamadas a la API del comercio.
        </Text>
        <HStack gap={2}>
          <Input
            readOnly
            fontFamily="mono"
            fontSize="sm"
            value={apiKey ? maskSecret(apiKey) : "No configurada"}
          />
          <IconButton
            aria-label="Copiar API key"
            variant="outline"
            disabled={!apiKey}
            onClick={() => apiKey && copy(apiKey)}
          >
            <Copy size={16} />
          </IconButton>
          <IconButton
            aria-label={apiKey ? "Regenerar API key" : "Crear API key"}
            variant="outline"
            loading={setApiKey.isPending}
            disabled={!merchantId}
            onClick={() => setApiKey.mutate({ rentalsMerchantId: merchantId })}
          >
            <RefreshCw size={16} />
          </IconButton>
        </HStack>
      </Box>

      <Box borderTopWidth="1px" borderColor="gray.200" pt={5}>
        <Text fontWeight={600} mb={1}>
          Webhook
        </Text>
        <Text fontSize="sm" color="gray.500" mb={3}>
          Al crear el webhook se genera un secret para validar la firma.
        </Text>
        <VStack gap={3} align="stretch">
          <Box>
            <Text fontSize="sm" fontWeight={500} mb={1}>
              URL
            </Text>
            <Input
              fontFamily="mono"
              fontSize="sm"
              placeholder="https://ejemplo.com/webhooks/palomma"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
            />
          </Box>
          <HStack justify="flex-end">
            <Button
              variant="outline"
              disabled={!webhook || removeWebhook.isPending}
              loading={removeWebhook.isPending}
              onClick={() =>
                removeWebhook.mutate({ rentalsMerchantId: merchantId })
              }
            >
              Quitar URL
            </Button>
            <Button
              disabled={!webhookUrl || setWebhook.isPending}
              loading={setWebhook.isPending}
              onClick={() =>
                setWebhook.mutate({
                  rentalsMerchantId: merchantId,
                  url: webhookUrl,
                })
              }
            >
              {webhook ? "Actualizar URL" : "Crear webhook"}
            </Button>
          </HStack>
          <Box>
            <Text fontSize="sm" fontWeight={500} mb={1}>
              Secret
            </Text>
            <HStack gap={2}>
              <Input
                readOnly
                fontFamily="mono"
                fontSize="sm"
                value={
                  webhook?.secret ? maskSecret(webhook.secret) : "No configurado"
                }
              />
              <IconButton
                aria-label="Copiar webhook secret"
                variant="outline"
                disabled={!webhook?.secret}
                onClick={() => webhook?.secret && copy(webhook.secret)}
              >
                <Copy size={16} />
              </IconButton>
              <IconButton
                aria-label="Regenerar webhook secret"
                variant="outline"
                loading={rotateWebhookSecret.isPending}
                disabled={!webhook}
                onClick={() =>
                  rotateWebhookSecret.mutate({
                    rentalsMerchantId: merchantId,
                  })
                }
              >
                <RefreshCw size={16} />
              </IconButton>
            </HStack>
          </Box>
        </VStack>
      </Box>
    </VStack>
  );
};

export default ApiTab;
